// "How points work": a friendly guide to the points behind Top contributors
// and the AI Champions Cup. Every number comes from lib/contributions, so the
// guide can't drift from the scoring.
import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Award, Heart, Lightbulb, Plus, Rocket, Sparkles, Trophy, Wrench, X } from "lucide-react";
import { LEVELS, POINTS, badgesFor, levelFor } from "../../lib/contributions";

const BADGE_ICONS = { idea: Lightbulb, tool: Sparkles, live: Rocket, likes: Heart } as const;

export function PointsGuide({ points, onClose }: { points?: number; onClose: () => void }) {
  const closeRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    closeRef.current?.focus();
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const level = points === undefined ? null : levelFor(points);
  const top = LEVELS[LEVELS.length - 1].from;
  const journey = [
    { icon: Lightbulb, label: "Idea submitted", add: POINTS.idea },
    { icon: Wrench, label: "Being built", add: POINTS.building },
    { icon: Rocket, label: "Live", add: POINTS.live },
  ];
  let running = 0;

  return (
    <div className="modal-backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal pg" role="dialog" aria-modal="true" aria-labelledby="pg-title">
        <div className="pg-hero">
          <span className="pg-hero-icon" aria-hidden>
            <Trophy size={26} />
          </span>
          <div>
            <h2 id="pg-title" className="pg-title">
              How points work
            </h2>
            <p className="pg-sub">Every idea, prompt, agent and app you add earns points for you and for your community.</p>
          </div>
          <button ref={closeRef} type="button" className="pg-close" onClick={onClose} aria-label="Close">
            <X size={18} aria-hidden />
          </button>
        </div>

        <div className="pg-body">
          <section aria-labelledby="pg-earn">
            <h3 id="pg-earn" className="pg-h">Ways to earn</h3>
            <ul className="pg-earn">
              <li className="pg-card pg-card--idea">
                <span className="pg-card-icon" aria-hidden>
                  <Lightbulb size={20} />
                </span>
                <span className="pg-card-pts">+{POINTS.idea}</span>
                <span className="pg-card-title">Submit an idea</span>
                <span className="pg-card-text">Any idea added to AI Initiatives.</span>
              </li>
              <li className="pg-card pg-card--tool">
                <span className="pg-card-icon" aria-hidden>
                  <Sparkles size={20} />
                </span>
                <span className="pg-card-pts">+{POINTS.tool}</span>
                <span className="pg-card-title">Share a tool</span>
                <span className="pg-card-text">A prompt, agent or app in the AI Library.</span>
              </li>
              <li className="pg-card pg-card--live">
                <span className="pg-card-icon" aria-hidden>
                  <Rocket size={20} />
                </span>
                <span className="pg-card-pts">+{POINTS.building + POINTS.live}</span>
                <span className="pg-card-title">Ship it</span>
                <span className="pg-card-text">Bonus as your idea gets built and goes live.</span>
              </li>
              <li className="pg-card pg-card--likes">
                <span className="pg-card-icon" aria-hidden>
                  <Heart size={20} />
                </span>
                <span className="pg-card-pts">+{POINTS.likes}</span>
                <span className="pg-card-title">Get liked</span>
                <span className="pg-card-text">For every 5 likes on your tools.</span>
              </li>
            </ul>
          </section>

          <section aria-labelledby="pg-journey">
            <h3 id="pg-journey" className="pg-h">An idea's journey</h3>
            <ol className="pg-journey">
              {journey.map(({ icon: Icon, label, add }) => {
                running += add;
                return (
                  <li key={label} className="pg-step">
                    <span className="pg-step-icon" aria-hidden>
                      <Icon size={18} />
                    </span>
                    <span className="pg-step-label">{label}</span>
                    <span className="pg-step-add">+{add}</span>
                    <span className="pg-step-total">{running} pts total</span>
                  </li>
                );
              })}
            </ol>
            <p className="pg-note">One great idea that goes live is worth {running} points.</p>
          </section>

          <section aria-labelledby="pg-levels">
            <h3 id="pg-levels" className="pg-h">
              Level up
              {level ? (
                <span className="pg-you-are">
                  You're Level {level.number} · {level.name} · {points} pts
                </span>
              ) : null}
            </h3>
            <ol className="pg-levels">
              {LEVELS.map((l, i) => {
                const reached = level ? level.number > i : false;
                const current = level?.number === i + 1;
                return (
                  <li key={l.name} className={`pg-level${reached ? " is-reached" : ""}${current ? " is-current" : ""}`}>
                    <span className="pg-level-num">{i + 1}</span>
                    <span className="pg-level-name">{l.name}</span>
                    <span className="pg-level-from">{l.from ? `${l.from}+ pts` : "Start here"}</span>
                  </li>
                );
              })}
            </ol>
            {level ? (
              <div className="pg-track" aria-hidden>
                <span style={{ width: `${Math.min(100, ((points ?? 0) / top) * 100)}%` }} />
              </div>
            ) : null}
          </section>

          <section aria-labelledby="pg-badges">
            <h3 id="pg-badges" className="pg-h">Badges</h3>
            <ul className="pg-badges">
              {badgesFor(undefined).map((b) => {
                const Icon = BADGE_ICONS[b.id as keyof typeof BADGE_ICONS];
                return (
                  <li key={b.id} className="pg-badge">
                    <span className="pg-badge-icon" aria-hidden>
                      <Icon size={18} />
                    </span>
                    <span className="pg-badge-name">{b.label}</span>
                    <span className="pg-badge-how">{b.hint}</span>
                  </li>
                );
              })}
            </ul>
          </section>

          <section className="pg-cup" aria-labelledby="pg-cup">
            <span className="pg-cup-icon" aria-hidden>
              <Award size={22} />
            </span>
            <div>
              <h3 id="pg-cup" className="pg-h pg-h--flush">The AI Champions Cup</h3>
              <p>
                Your points also count for your community. Each quarter the six communities race for the Cup on the
                dashboard, and the leaderboard starts fresh every quarter. Your own level keeps everything you've ever added.
              </p>
            </div>
          </section>

          <section aria-labelledby="pg-faq">
            <h3 id="pg-faq" className="pg-h">Good to know</h3>
            <dl className="pg-faq">
              <div>
                <dt>Who gets the credit?</dt>
                <dd>For an idea, the person named in "Your name". For a tool, whoever shared it.</dd>
              </div>
              <div>
                <dt>What doesn't count?</dt>
                <dd>Tools that are draft, rejected or archived. Closed ideas keep their {POINTS.idea} points but earn no bonus.</dd>
              </div>
              <div>
                <dt>When do points update?</dt>
                <dd>Automatically, from AI Initiatives and the AI Library. Nothing to claim.</dd>
              </div>
            </dl>
          </section>
        </div>

        <div className="pg-foot">
          <span>
            {!points || !level
              ? "Ready to earn your first points?"
              : level.next
                ? `${level.next.from - points} pts to ${level.next.name}. Keep going!`
                : "You've reached the top level. Legend!"}
          </span>
          <span className="pg-foot-actions">
            <Link className="ca-btn" to="/library" onClick={onClose}>
              <Sparkles size={14} aria-hidden /> Share a tool
            </Link>
            <Link className="ca-btn ca-btn--primary" to="/initiatives?new=1" onClick={onClose}>
              <Plus size={14} aria-hidden /> Submit an idea
            </Link>
          </span>
        </div>
      </div>
    </div>
  );
}
